import { LoadingOutlined } from '@ant-design/icons';
import { Select } from 'antd';
import React, { ReactElement, useCallback, useState } from 'react';

export interface ISearchOption {
    label: string;
    value: string;
}

export default function SearchSelect(props: {
    defaultText?: string;
    loading: boolean;
    onBlur: () => void;
    onSearch: (keyword: string) => void;
    onSelect: (value: ISearchOption['value'], option: ISearchOption) => void;
    onSearchClear: () => void;
    options: ISearchOption[];
    value: string;
}): ReactElement {
    const {
        defaultText,
        loading,
        onSelect: parentOnSelect,
        onSearch: parentOnSearch,
        onBlur: parentOnBlur,
        onSearchClear,
        options,
        value,
    } = props;
    // Flag to stop antd from resetting search(sending '') on first render
    const [ searchEntered, setSearchEntered ] = useState<boolean>(false);
    // Flag to stop antd from resetting search(sending '') on select
    const [ selected, setSelected ] = useState<boolean>(false);
    // Flag to Open/close the dropdown as antd has some default open settings on clear
    const [ open, setOpen ] = useState<boolean>(false);
    const [ suffixIcon, setSuffixIcon ] = useState<React.ReactElement>(null);
    // Search Text has initial state, but most state is self contained, simplifying overall implementation for now
    const [ searchText, setSearchText ] = useState<string>(value !== null ? defaultText : '');

    const onSelect: (value: string, option: ISearchOption) => void = useCallback((value: string, option: ISearchOption) => {
        parentOnSelect(value, option);
        setSearchText(option.label);
        onSearchClear();
        setOpen(false);
        setSelected(true);
        setSuffixIcon(null);
    }, [
        onSearchClear,
        parentOnSelect,
        setSearchText,
        setSelected,
    ]);

    const onSearch: (keyword: string) => void = useCallback((keyword: string) => {
        // Avoid antd empty string search on initialization and selection
        if (searchEntered && !(keyword === '' && selected)) {
            setSuffixIcon(<LoadingOutlined />);
            setSearchText(keyword);
            setOpen(true);
            if (keyword) {
                parentOnSearch(keyword);
            }
        }

        // Reset on selection in the case of empty string search by user
        if (selected && keyword === '') {
            setSelected(false);
        }
    }, [
        parentOnSearch,
        searchEntered,
        selected,
        setSearchText,
        setSelected,
    ]);

    const onFocus: () => void = useCallback(() => {
        setSearchEntered(true);
    }, [
        setSearchEntered,
    ]);

    const onBlur: () => void = useCallback(() => {
        parentOnBlur();
        setSearchEntered(false);
        setSearchText(value !== null ? defaultText : '');
        onSearchClear();
        setOpen(false);
        setSuffixIcon(null);
    }, [
        defaultText,
        onSearchClear,
        parentOnBlur,
        setSearchEntered,
        setSearchText,
        value,
    ]);

    return (
        <Select
            autoClearSearchValue={false}
            filterOption={false}
            loading={loading}
            notFoundContent={null}
            open={open}
            options={options}
            searchValue={searchText}
            showSearch={true}
            suffixIcon={suffixIcon}
            onBlur={onBlur}
            onFocus={onFocus}
            onSearch={onSearch}
            onSelect={onSelect}
        />
    );
}
